import { Component, Signal, ViewEncapsulation, computed, inject, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ActivatedRoute, RouterModule } from '@angular/router';
import { toSignal } from '@angular/core/rxjs-interop';
import { switchMap, map, startWith, catchError, of } from 'rxjs';
import {
    CeBreadcrumbComponent,
    CeBreadcrumbRouteEntry,
    CeButtonComponent,
    CeContextMenuComponent,
    CeContextMenuTriggerDirective,
    CeIconComponent,
    CeInlineBannerComponent,
    CeListComponent,
    CeListItemComponent,
    CeListItemPrefixDirective,
    CePillComponent,
    CeSkeletonLoaderComponent,
    CeSnackbarService,
    fadeInOnEnterAnimation,
} from '@kempower-nx-workspace/shared/kp-design-system';
import { Clipboard } from '@angular/cdk/clipboard';
import { capitalize as _capitalize } from 'lodash-es';
import { ContentCdnService, Entry } from '@kempower-nx-workspace/chargeye-content';
import { CeTemplateViewHeaderComponent } from '@kempower-nx-workspace/layout-templates';
import { AuthenticationService } from '@kempower-nx-workspace/ce-authentication';
import { environment } from '@kempower-nx-workspace/ce-environment';
import { LightBoxComponent } from '../../ui/light-box/light-box.component';

type DetailState = {
    entry: Entry | null;
    isLoading: boolean;
    isError: boolean;
    breadCrumbData: CeBreadcrumbRouteEntry[];
};

@Component({
    selector: 'ce-app-product-update',
    providers: [ContentCdnService, CeSnackbarService],
    imports: [
        CommonModule,
        RouterModule,
        CeTemplateViewHeaderComponent,
        CeBreadcrumbComponent,
        CeInlineBannerComponent,
        CeButtonComponent,
        CeIconComponent,
        CePillComponent,
        CeSkeletonLoaderComponent,
        CeContextMenuComponent,
        CeContextMenuTriggerDirective,
        CeListComponent,
        CeListItemComponent,
        CeListItemPrefixDirective,
        LightBoxComponent,
    ],
    animations: [fadeInOnEnterAnimation],
    encapsulation: ViewEncapsulation.None,
    templateUrl: './product-updates.component.html',
    styleUrl: './product-update.component.scss',
})
export class ProductUpdatesComponent {
    readonly #cdnService = inject(ContentCdnService);
    readonly #authService = inject(AuthenticationService);
    readonly clipboard = inject(Clipboard);
    readonly snackBar = inject(CeSnackbarService);
    readonly activatedRoute = inject(ActivatedRoute);

    readonly isSysAdmin = this.#authService.getCsmsAuthProfilePermissions().isSystemAdmin ?? false;

    readonly #entryData = toSignal(
        this.activatedRoute.paramMap.pipe(
            map((params) => params.get('productUpdateId') ?? ''),
            switchMap((id) =>
                this.#cdnService.getEntry(id).pipe(
                    map((entry) => ({ entry, isLoading: false, isError: false })),
                    startWith({ entry: null as Entry | null, isLoading: true, isError: false }),
                    catchError(() => of({ entry: null as Entry | null, isLoading: false, isError: true }))
                )
            )
        ),
        { initialValue: { entry: null as Entry | null, isLoading: true, isError: false } }
    );

    pageState: Signal<DetailState> = computed(() => {
        const { entry, isLoading, isError } = this.#entryData();
        const type = entry?.type ?? '';
        const typeLabels: Record<string, string> = { chargeye: 'ChargEye', firmware: 'Firmware', all: 'All' };
        const breadCrumbData: CeBreadcrumbRouteEntry[] = [{ path: '/help/product-updates', title: 'Product updates' }];
        if (type) {
            breadCrumbData.push({ path: `/help/product-updates/${type}`, title: typeLabels[type] ?? _capitalize(type) });
        }
        return {
            entry,
            isLoading,
            isError,
            breadCrumbData,
        };
    });

    copyUrl(): void {
        this.clipboard.copy(window.location.href);
        this.snackBar.showSnackbar({ message: 'Copied URL', duration: 3000 });
    }

    editInAdmin(): void {
        const id = this.pageState().entry?.id;
        if (!id) return;
        const stage = environment.contentCdn?.stage ?? 'dev';
        const subdomain = stage === 'prod' ? 'contentless' : `contentless.${stage}`;
        window.open(`https://${subdomain}.kempower.io/admin/entries/${id}/edit`, '_blank');
    }

    formatDate(dateStr: string): string {
        return new Date(dateStr).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
    }

    readonly lightBox = signal<{ open: boolean; src: string; title: string; description: string }>({
        open: false,
        src: '',
        title: '',
        description: '',
    });

    onContentClick(event: MouseEvent): void {
        const target = event.target as HTMLElement;
        if (target.tagName !== 'IMG') return;
        const img = target as HTMLImageElement;
        this.lightBox.set({
            open: true,
            src: img.src,
            title: img.getAttribute('title') ?? img.alt ?? '',
            description: img.alt ?? '',
        });
    }

    onLightBoxStateChange(state: 'open' | 'close'): void {
        if (state === 'close') {
            this.lightBox.update((current) => ({ ...current, open: false }));
        }
    }
}
